import { HsVector2 } from '../../math-new/objects/vector2.class';
import { HsControlActionType } from '../../tools-new/actions/control-action-type.enum';
import { HsKeyboardControlAction } from '../../tools-new/actions/keyboard-control-action.class';
import { HsMouseMoveControlAction } from '../../tools-new/actions/mouse-move-control-action.class';
import { HsScrollControlAction } from '../../tools-new/actions/scroll-control-action.class';
import type { HsControlActionsHandler } from '../../tools-new/camera/control-actions-handler.class';
import { HsTouchGestureRecognizer } from '../../tools-new/gestures/touch-gesture-recognizer.class';
import type { HsDisposable } from '../extensions/disposable.interface';

/** What the input drives; `HsHypersceneViewer` provides it. */
export interface HsViewerInputTarget {
  readonly controls: HsControlActionsHandler;
  readonly surface: { readonly canvas: HTMLCanvasElement };
  requestRender(): void;
}

export interface HsViewerInputOptions {
  /**
   * `KeyboardEvent.key` to camera action; keys missing from the map are ignored. Single characters are looked
   * up in lower case, so Shift and Caps Lock do not change the action.
   */
  keyMap?: ReadonlyMap<string, HsControlActionType>;
  /** Action of a drag per mouse button (0 left, 1 middle, 2 right); null ignores the button. */
  dragActions?: readonly (HsControlActionType | null)[];
  /** Host reactions to input beyond camera navigation. */
  hooks?: HsViewerInputHooks;
  /** Touch recognizer to use, for hosts that configure pivots or pan scales (default a new one). */
  touchGestures?: HsTouchGestureRecognizer;
}

/** Host reactions to input; every hook is optional. */
export interface HsViewerInputHooks {
  /** A mouse button went down on the canvas; tools such as measurements or markers start here. */
  pointerDown?(event: MouseEvent): void;
  /** The left button went down and up on the canvas without dragging: a selection click. */
  click?(event: MouseEvent): void;
  /** The left button was double-clicked on the canvas; viewers focus the point under it. */
  doubleClick?(event: MouseEvent): void;
  /** A mapped key changed state; for example a modifier that moves the rotation center. */
  key?(action: HsControlActionType, pressed: boolean): void;
  /**
   * Scales wheel and key movement at a client point, for speeds that follow the distance to the scene; key
   * movement passes its action.
   */
  speedScale?(source: 'wheel' | 'key', clientX: number, clientY: number, action?: HsControlActionType): number;
  /** A touch gesture started on the canvas. */
  touchStart?(): void;
  /** A drag or touch gesture ended. */
  navigationEnd?(): void;
}

const DEFAULT_DRAG_ACTIONS: readonly (HsControlActionType | null)[] = [
  HsControlActionType.LeftMove,
  HsControlActionType.MiddleMove,
  HsControlActionType.RightMove,
];

/**
 * Turns mouse, wheel, keyboard and touch events on a viewer canvas into camera actions. A drag that
 * starts on the canvas keeps steering after the pointer leaves it; the wheel and keys act while the
 * canvas has focus or the pointer is over it. A right-button drag does not open the browser's context menu.
 */
export class HsViewerInput implements HsDisposable {
  private readonly canvas: HTMLCanvasElement;
  private readonly document: Document;
  private readonly touchGestures: HsTouchGestureRecognizer;
  private readonly keyMap: ReadonlyMap<string, HsControlActionType>;
  private readonly dragActions: readonly (HsControlActionType | null)[];
  private readonly hooks: HsViewerInputHooks;
  private readonly removers: (() => void)[] = [];
  private mouseButton: number | null = null;
  private clickCandidate: MouseEvent | null = null;
  /** The last right press moved, so the context menu that browsers open on release must not show. */
  private rightDragged: boolean = false;
  private readonly pointer: { clientX: number; clientY: number; pageX: number; pageY: number } = {
    clientX: 0,
    clientY: 0,
    pageX: 0,
    pageY: 0,
  };

  public constructor(private readonly target: HsViewerInputTarget, options: HsViewerInputOptions = {}) {
    this.canvas = target.surface.canvas;
    this.document = this.canvas.ownerDocument;
    this.touchGestures = options.touchGestures ?? new HsTouchGestureRecognizer(target.controls);
    this.keyMap = options.keyMap ?? new Map();
    this.dragActions = options.dragActions ?? DEFAULT_DRAG_ACTIONS;
    this.hooks = options.hooks ?? {};
    this.listen('mousedown', (event: MouseEvent) => this.onMouseDown(event));
    this.listen('mouseup', (event: MouseEvent) => this.onMouseUp(event));
    this.listen('mousemove', (event: MouseEvent) => this.onMouseMove(event));
    this.listen('dblclick', (event: MouseEvent) => {
      if (event.target === this.canvas && event.button === 0) {
        this.hooks.doubleClick?.(event);
      }
    });
    this.listen('contextmenu', (event: MouseEvent) => this.onContextMenu(event));
    this.listen('wheel', (event: WheelEvent) => this.onWheel(event), { passive: false });
    this.listen('keydown', (event: KeyboardEvent) => this.onKey(event, true));
    this.listen('keyup', (event: KeyboardEvent) => this.onKey(event, false));
    for (const type of ['pointerdown', 'pointermove', 'pointerup', 'pointercancel'] as const) {
      this.listen(type, (event: PointerEvent) => this.onPointer(event));
    }
  }

  /** The mouse button held since it went down on the canvas, or null. */
  public get button(): number | null {
    return this.mouseButton;
  }

  /** Touch points of the running gesture on the canvas. */
  public get touches(): number {
    return this.touchGestures.activePointerCount;
  }

  /** The last mouse position, in client and page coordinates. */
  public get lastPointer(): Readonly<{ clientX: number; clientY: number; pageX: number; pageY: number }> {
    return this.pointer;
  }

  /** Ends the current drag without a click, for example when the host moves the camera itself. */
  public cancel(): void {
    this.mouseButton = null;
    this.clickCandidate = null;
  }

  public dispose(): void {
    this.removers.splice(0).forEach((remove: () => void) => remove());
  }

  private listen<K extends keyof DocumentEventMap>(
    type: K,
    handler: (event: DocumentEventMap[K]) => void,
    options?: AddEventListenerOptions
  ): void {
    this.document.addEventListener(type, handler, options);
    this.removers.push(() => this.document.removeEventListener(type, handler, options));
  }

  private onMouseDown(event: MouseEvent): void {
    const onCanvas: boolean = event.target === this.canvas;
    this.mouseButton = onCanvas ? event.button : null;
    this.clickCandidate = onCanvas && event.button === 0 ? event : null;
    this.rightDragged = false;
    if (onCanvas && event.button === 1 && this.dragActions[1] !== null && this.dragActions[1] !== undefined) {
      // Windows browsers start auto-scrolling on a middle press, which would steal the pan drag.
      event.preventDefault();
    }
    if (onCanvas) {
      this.canvas.focus({ preventScroll: true });
      this.hooks.pointerDown?.(event);
      this.target.requestRender();
    } else if (this.document.activeElement === this.canvas) {
      this.canvas.blur();
    }
  }

  private onMouseUp(event: MouseEvent): void {
    const click: MouseEvent | null = this.clickCandidate;
    const dragging: boolean = this.mouseButton !== null;
    this.clickCandidate = null;
    this.mouseButton = null;
    if (dragging) {
      this.hooks.navigationEnd?.();
    }
    if (click !== null && event.button === 0) {
      this.hooks.click?.(click);
    }
  }

  private onMouseMove(event: MouseEvent): void {
    this.pointer.clientX = event.clientX;
    this.pointer.clientY = event.clientY;
    this.pointer.pageX = event.pageX;
    this.pointer.pageY = event.pageY;
    if (event.movementX !== 0 || event.movementY !== 0) {
      // A drag is not a click.
      this.clickCandidate = null;
      this.rightDragged ||= this.mouseButton === 2;
    }
    const action: HsControlActionType | null | undefined =
      this.mouseButton === null ? null : this.dragActions[this.mouseButton];
    if (action === null || action === undefined || (event.movementX === 0 && event.movementY === 0)) {
      return;
    }
    this.target.controls.updateControlAction(
      new HsMouseMoveControlAction(action, new HsVector2(-event.movementX, -event.movementY))
    );
    this.target.requestRender();
  }

  /**
   * Keeps the context menu off a right drag. Windows opens the menu on release, so a right click that did not move
   * still gets it; macOS and Linux open it on press, before a drag can be told from a click, so over the canvas
   * it stays closed while right drags navigate.
   */
  private onContextMenu(event: MouseEvent): void {
    if (this.dragActions[2] === null || this.dragActions[2] === undefined) {
      return;
    }
    const openedOnPress: boolean = this.mouseButton === 2 && event.target === this.canvas;
    if (openedOnPress || this.rightDragged) {
      event.preventDefault();
    }
    if (!openedOnPress) {
      this.rightDragged = false;
    }
  }

  private onWheel(event: WheelEvent): void {
    if (this.document.activeElement !== this.canvas && event.target !== this.canvas) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.pointer.clientX = event.clientX;
    this.pointer.clientY = event.clientY;
    // Lines and pages scroll by their size, so line-based mice zoom as far as pixel-based ones.
    const pageHeight: number = this.canvas.clientHeight > 0 ? this.canvas.clientHeight : 1;
    const unit: number = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? pageHeight : 1;
    const scale: number = this.hooks.speedScale?.('wheel', event.clientX, event.clientY) ?? 1;
    this.target.controls.updateControlAction(
      new HsScrollControlAction(new HsVector2(event.clientX, event.clientY), ((event.deltaY * unit) / 10) * scale)
    );
    this.target.requestRender();
  }

  private onKey(event: KeyboardEvent, pressed: boolean): void {
    // Keys also steer while a drag that started on the canvas continues outside it.
    if (this.document.activeElement !== this.canvas && this.mouseButton === null) {
      return;
    }
    const key: string = event.key.length === 1 ? event.key.toLowerCase() : event.key;
    const action: HsControlActionType | undefined = this.keyMap.get(key) ?? this.keyMap.get(event.key);
    if (action === undefined) {
      return;
    }
    event.preventDefault();
    this.hooks.key?.(action, pressed);
    const scale: number = pressed
      ? this.hooks.speedScale?.('key', this.pointer.clientX, this.pointer.clientY, action) ?? 1
      : 1;
    this.target.controls.updateControlAction(new HsKeyboardControlAction(action, pressed, scale));
    this.target.requestRender();
  }

  private onPointer(event: PointerEvent): void {
    let handled: boolean;
    switch (event.type) {
      case 'pointerdown':
        handled = event.target === this.canvas && this.touchGestures.pointerDown(event);
        if (handled) {
          this.hooks.touchStart?.();
          // Capture throws for pointers that are no longer active; the gesture still works over the canvas.
          try {
            this.canvas.setPointerCapture(event.pointerId);
          } catch {
            // Ignored on purpose.
          }
        }
        break;
      case 'pointermove':
        handled = this.touchGestures.pointerMove(event);
        break;
      case 'pointerup':
        handled = this.touchGestures.pointerUp(event);
        break;
      default:
        handled = this.touchGestures.pointerCancel(event);
    }
    if (handled) {
      event.preventDefault();
      this.target.requestRender();
      if (event.type !== 'pointerdown' && event.type !== 'pointermove') {
        if (this.canvas.hasPointerCapture(event.pointerId)) {
          this.canvas.releasePointerCapture(event.pointerId);
        }
        if (this.touchGestures.activePointerCount === 0) {
          this.hooks.navigationEnd?.();
        }
      }
    }
  }
}
